import React from 'react';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import type { CancellationInfoKey } from './types';

interface Props {
  infoKey: CancellationInfoKey;
}

const STEP_PREFIX = /^\d+[.)]\s*/;

/**
 * Splits the translated steps into list items. The copy is shared with the mobile apps and
 * carries its own numbering ("1. Open…\n2. Tap…"); that prefix is stripped so the list can
 * number itself with a hanging indent, like the Figma `<ol>`. If a translation ever comes
 * without numbering, the text is shown as-is, line by line.
 */
export const splitSteps = (copy: string): { steps: string[]; isNumbered: boolean } => {
  const lines = copy
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const isNumbered = lines.length > 0 && lines.every((line) => STEP_PREFIX.test(line));
  return {
    steps: isNumbered ? lines.map((line) => line.replace(STEP_PREFIX, '')) : lines,
    isNumbered,
  };
};

/**
 * "How to cancel" box shown under a store / BLIK subscription card. Those subscriptions can
 * only be cancelled outside the app, so instead of a button the user gets the steps
 * (`accessCard.<key>.info.copy1..3`, shared with the mobile apps).
 */
const AccessCancellationInfo: React.FC<Props> = ({ infoKey }) => {
  const { t } = useTranslator();
  const {
    images: { infoIcon },
  } = useAppContext();

  const { steps, isNumbered } = splitSteps(t(`accessCard.${infoKey}.info.copy3`));

  return (
    <div className="access-cancellation-info" data-testid="access-cancellation-info">
      <div className="access-cancellation-info__lead">
        <img src={infoIcon} alt="" className="access-cancellation-info__icon" />
        <p className="access-cancellation-info__text">{t(`accessCard.${infoKey}.info.copy1`)}</p>
      </div>
      <div className="access-cancellation-info__how-to">
        <p className="access-cancellation-info__heading">{t(`accessCard.${infoKey}.info.copy2`)}</p>
        {isNumbered ? (
          <ol className="access-cancellation-info__steps" data-testid="access-cancellation-steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : (
          <p className="access-cancellation-info__text" data-testid="access-cancellation-steps">
            {steps.map((step, index) => (
              <React.Fragment key={step}>
                {index > 0 && <br />}
                {step}
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    </div>
  );
};

export default AccessCancellationInfo;
